
import { useState } from "react";
import { uploadImage } from "@/lib/image-utils";

export interface UseImageUploadProps {
  initialUrl?: string;
  onComplete?: (url: string) => void;
}

export const useImageUpload = ({ initialUrl = "", onComplete }: UseImageUploadProps = {}) => {
  const [imageUrl, setImageUrl] = useState(initialUrl);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async (file: File): Promise<string | null> => {
    try {
      setUploading(true);
      setError(null);
      
      const { url, error } = await uploadImage(file);
      
      if (error) {
        throw new Error(error.message || "Failed to upload image");
      }
      
      if (!url) {
        throw new Error("No URL returned from upload");
      }
      
      setImageUrl(url);
      
      if (onComplete) {
        onComplete(url);
      }
      
      return url;
    } catch (err: any) {
      setError(err.message || "Failed to upload image");
      console.error("Image upload error:", err);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setImageUrl("");
    setError(null);
    
    if (onComplete) {
      onComplete("");
    }
  };

  return {
    imageUrl,
    uploading,
    error,
    handleUpload,
    handleRemove,
  };
};
